import * as d3 from 'd3';
import {format} from 'd3';

import {COLOR_EXTRACTED_85} from 'src/styles/colors';
import {ApplicationStats} from 'src/types';

interface Stats {
    date: Date,
    value: number,
}

export const getMostRecentAgentType = (applicationStats: ApplicationStats) => {
  let name = 'None';

  for (const value of Object.values(applicationStats.agentTypeDates)) {
    if (value.lastScan === applicationStats.lastAgentDate) {
      name = value.name;
    }
  }

  return name;
};

const f = format(',');
const HEIGHT = 100;
const color = COLOR_EXTRACTED_85;

export const renderGraph = (stats: Array<Stats>, svgDom: SVGElement, size: {width: number | null, height: number | null}) => {
  const width = size.width || 600;
  const margin = {
    top: 20,
    right: 5,
    bottom: 20,
    left: 30,
  };

  if (stats.length <= 0) {
    return;
  }

  const svgEl = d3.select<SVGElement, {y: number}>(svgDom);
  svgEl.selectAll('*').remove(); // Clear svg content before adding new elements

  svgEl.append('linearGradient')
      .attr('id', 'area-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '75%')
      .selectAll('stop')
      .data([
        {offset: '0%', color: color},
        {offset: '100%', color: 'transparent'},
      ])
      .enter().append('stop')
      .attr('offset', function(d) {
        return d.offset;
      })
      .attr('stop-color', function(d) {
        return d.color;
      });

  const x = d3.scaleUtc()
      .domain(d3.extent<Stats, Date>(stats, (d) => d.date) as [Date, Date])
      .range([margin.left, width - margin.right]);

  const y = d3.scaleLinear()
      .domain([0, 100]).nice()
      .range([HEIGHT - margin.bottom, margin.top]);

  const line = d3.line<Stats>()
      .defined((d) => !isNaN(d.value))
      .x((d) => x(d.date) as number)
      .y((d) => y(d.value) as number);

  const area = d3.area<Stats>()
      .x((d) => x(d.date) as number)
      .y0(HEIGHT)
      .y1((d) => y(d.value) as number);

  svgEl.append('g')
      .call((g) => g
          .attr('transform', `translate(0,${HEIGHT - margin.bottom})`)
          .call(d3.axisBottom(x).ticks(width / 80).tickSizeOuter(0).tickSize(0)));

  svgEl.append('g')
      .call((g) => g
          .attr('transform', `translate(${margin.left},0)`)
          .call(d3.axisLeft(y).ticks(3).tickSize(0))
          .call((g) => g.select('.domain').remove())
          .call((g) => g.select('.tick:last-of-type text').clone()
              .attr('x', 3)
              .attr('text-anchor', 'start')
              .attr('font-weight', 'bold')
              .text( (data) => data.y)));

  svgEl.append('path')
      .datum(stats)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 1.5)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .attr('d', line);

  svgEl.append('path')
      .datum(stats)
      .attr('class', 'area')
      .attr('d', area);

  svgEl.select('.domain').remove();
};

export const formatDashboardNumbers = (value: number) => {
  if ((value > 10000)) {
    const roundValue = value.toString().substring(0, 2);
    return `+${roundValue}K`;
  } else {
    return f(value || 0);
  }
};
